import { responseProjectEtag } from "./project-etag";
import { csrfHeaders, type AuthenticatedSession } from "./session";
import { isAuthorizationFailure } from "./session-recovery";

export const uniqueImportedName = (sourceName: string, names: string[]): string => {
    const base = sourceName.trim().replace(/\.sb3$/iu, "").trim().slice(0, 100) || "Projekt";
    const taken = new Set(names);
    if (!taken.has(base)) return base;
    for (let index = 2; ; index++) {
        const suffix = ` (${index})`;
        const candidate = `${base.slice(0, 100 - suffix.length)}${suffix}`;
        if (!taken.has(candidate)) return candidate;
    }
};

export const importCloudProject = async (
    content: Blob,
    sourceName: string,
    session: AuthenticatedSession,
    fetchImpl: typeof fetch = fetch,
): Promise<string> => {
    const name = uniqueImportedName(sourceName, []);
    const created = await fetchImpl("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders(session) },
        body: JSON.stringify({ name, allocateName: true }),
    });
    if (isAuthorizationFailure(created)) throw new Error("AUTHORIZATION");
    if (!created.ok) {
        if (created.status === 409) throw new Error("PROJECT_LIMIT_REACHED");
        throw new Error("IMPORT_CREATE_FAILED");
    }
    const { project } = (await created.json()) as { project: { id: string } };
    const etag = responseProjectEtag(created);
    try {
        const saved = await fetchImpl(`/api/projects/${project.id}/content`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/x.scratch.sb3",
                "If-Match": etag,
                ...csrfHeaders(session),
            },
            body: content,
        });
        if (isAuthorizationFailure(saved)) throw new Error("AUTHORIZATION");
        if (!saved.ok) throw new Error("IMPORT_CONTENT_FAILED");
        return project.id;
    } catch (error) {
        try {
            await fetchImpl(`/api/projects/${project.id}`, {
                method: "DELETE",
                headers: { "If-Match": etag, ...csrfHeaders(session) },
            });
        } catch {
            // An empty record is safer than deleting another version after a conflicting write.
        }
        throw error;
    }
};
